/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../common/constants';
import {CustomButtonCellComponent} from '../../../core/components/custom-button-cell/custom-button-cell.component';
import {AutocompleteFilterComponent} from '../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {GfsUserDtoModel} from '../../../models/gfs-user-dto.model';
import {ExcelExportService} from '../../../services/excel-export.service';
import {GfsUserService} from '../../../services/gfs-user.service';
import {ViewUserMapPopupComponent} from './view-user-map-popup/view-user-map-popup.component';

@Component({
    selector: 'app-user-maintenance',
    templateUrl: './user-maintenance.component.html',
    styleUrls: ['./user-maintenance.component.scss'],
    standalone: false
})
export class UserMaintenanceComponent implements OnInit {
  selectedUser: GfsUserDtoModel;
  gfsUsers: GfsUserDtoModel[] = [];

  showSpinner: boolean;
  noData: boolean;

  userColumns: ColDef[] = [
    {field: 'id', headerName: 'User ID', filter: AutocompleteFilterComponent},
    {field: 'firstName', headerName: 'First Name', filter: AutocompleteFilterComponent},
    {field: 'lastName', headerName: 'Last Name', filter: AutocompleteFilterComponent},
    {field: 'role', headerName: 'Security Level'},
    {
      field: 'regions', headerName: 'Regions',
      cellRenderer: CustomButtonCellComponent,
      cellRendererParams: {
        icon: 'visibility',
        onClick: this.openViewUserMapDialog.bind(this)
      }
    },
  ];

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  };

  constructor(public snackBar: FabMessageService,
              private readonly gfsUserService: GfsUserService,
              readonly dialog: MatDialog,
              readonly router: Router,
              public readonly excelService: ExcelExportService) {
  }

  ngOnInit(): void {
    this.loadUsers();
  }

  /**
   * Fetches all users from the service and updated table
   */
  loadUsers(): void {
    this.showSpinner = true;
    this.gfsUserService.getAllUsers().subscribe(
      (data: GfsUserDtoModel[]) => {
        this.gfsUsers = data;
      },
      (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error);
      },
      () => {
        this.showSpinner = false;
        this.noData = (this.gfsUsers.length === 0);
      });
  }

  /**
   * Event for selecting a user
   * @param event the event object
   */
  onSelect(event) {
    if (event.node.selected) {
      this.selectedUser = (event.data);
    } else if (event.data.id === this.selectedUser.id) {
      this.selectedUser = null;
    }
  }

  /**
   * Navigates to add user page
   */
  openAddUser(): void {
    this.router.navigate(['/add-user']);
  }

  /**
   * Navigates to edit user page when a user is selected from a row
   */
  openEditUser(): void {
    const userToEdit = this.selectedUser.id;
    this.router.navigate(['/edit-user'], {queryParams: {userId: userToEdit}});
  }

  /**
   * Opens dialog popup to view user map
   * @param rowData - parameter from custom cell component to pass to pop up
   */
  openViewUserMapDialog(rowData: any): void {
    const userGmid = rowData.id;
    this.dialog.open(ViewUserMapPopupComponent, {
      height: '80%',
      width: '60%',
      data: {userId: userGmid}
    });
  }

  /**
   * After user confirms, calls user service method to delete user
   */
  deleteUser(): void {
    const userToDel = this.selectedUser;

    if (confirm(`Are you sure you want to delete user ${userToDel.id} ?`)) {
      this.showSpinner = true;
      this.gfsUserService.deleteUser(userToDel).subscribe({
        error: (error) => {
          this.showSpinner = false;
          this.snackBar.displayError(error);
        },
        complete: () => {
          this.showSpinner = false;
          this.snackBar.displaySuccess('Successfully removed user.', DISMISS_MSG);
          this.selectedUser = null;
          this.loadUsers();
        }
      });
    }
  }

  /**
   * Function to export user report to Excel file
   */
  getUserReport(): void {
    this.showSpinner = true;
    this.gfsUserService.getUserReport().subscribe({
      next: (file: any) => {
        const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');

        this.excelService.exportToExcelFromServer(file, "UserReport_" + currDate);
      },
      error: () => {
        this.snackBar.displayError('Error with exporting data.');
        this.showSpinner = false;
      },
      complete: () => {
        this.showSpinner = false;
      }
    });
  }
}
